/**
 * Registry of dynamic placeholder keys that are resolved at request time from
 * the landing URL's query string, rather than from CMS content.
 *
 * Each entry maps a Sanity placeholder key to a URL query parameter name and
 * optionally carries a `resolver` callback for custom resolution logic (e.g.
 * feature-flag gates). When no `resolver` is provided, the standard behaviour
 * applies: decode the `queryParam` value from the request URL.
 *
 * Resolution lives in hi-www (`apps/hi-www/src/lib/placeholder/runtime/`).
 * Studio reads this registry to populate the Dynamic Placeholder dropdown so
 * editors can discover available keys.
 */

/**
 * Runtime data passed to each definition's `resolver` callback.
 * `paramContext` is a generic bag of values keyed by query-param name,
 * populated by the caller (e.g. from a feature flag) so individual
 * resolver callbacks can access them without the schema package depending
 * on hi-www internals.
 */
type PlaceholderResolveContext = {
  paramContext?: Record<string, unknown>;
};

type RuntimeQueryPlaceholderDefinition = {
  /** The placeholder key editors reference on a `dynamicPlaceholder` mark. */
  placeholderKey: string;
  /** The URL query parameter to read the value from at request time. */
  queryParam: string;
  /** Category label used in the Studio dropdown (with icon prefix). */
  category: string;
  /** Human-readable title shown in the Studio dropdown. */
  title: string;
  /** Optional description shown in the Studio dropdown. */
  description?: string;
  /**
   * Custom resolution logic for this placeholder.
   *
   * Return a `string` to set the placeholder value, or `undefined` to fall
   * back to the Sanity Fallback Text configured for this mark.
   *
   * When omitted, standard behaviour applies: decode this definition's
   * `queryParam` from `searchParams` and emit the result when non-empty.
   *
   * `defaultResolve` is a convenience helper injected by the calling loop
   * that performs the standard `getSearchParam` + `decodeQueryParamValue`
   * logic for this definition's own `queryParam`.
   */
  resolver?: (
    searchParams: Record<string, string | string[] | undefined> | undefined,
    context: PlaceholderResolveContext,
    defaultResolve: () => string | undefined,
  ) => string | undefined;
};

const RUNTIME_QUERY_PLACEHOLDER_REGISTRY: readonly RuntimeQueryPlaceholderDefinition[] =
  [
    {
      category: "🔗 URL Parameters",
      description:
        "Paid-search keyword from the landing URL `utm_term` query parameter. Falls back to the per-mark Fallback Text when missing or empty. Only resolves when the `utm_adgroup` query parameter is present and listed in the utm_term adgroup allowlist feature flag.",
      placeholderKey: "utm_term",
      queryParam: "utm_term",
      resolver: (searchParams, { paramContext = {} }, defaultResolve) => {
        const allowed = paramContext["utm_adgroup_allowlist"];
        if (!Array.isArray(allowed)) {
          return undefined;
        }
        const raw = searchParams?.["utm_adgroup"];
        const adgroup = Array.isArray(raw) ? raw[0] : raw;
        if (!adgroup || !allowed.some((id) => String(id) === String(adgroup))) {
          return undefined;
        }
        return defaultResolve();
      },
      title: "UTM Term",
    },
  ];

export type { PlaceholderResolveContext, RuntimeQueryPlaceholderDefinition };

export { RUNTIME_QUERY_PLACEHOLDER_REGISTRY };
